import React, { useState } from 'react';
import { Outlet, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  Collapse
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  CloudQueue as CloudIcon,
  Security as SecurityIcon,
  Map as MapIcon,
  MonitorHeart as MonitorIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Category as CategoryIcon,
  ViewList as ViewListIcon,
  SwapHoriz as MigrationsIcon,
} from '@mui/icons-material';
import config from '../config';
// import { FEATURES, hasFeatureClaim } from '../utils/constants';  // Moved to Option A: Entra-only config

const drawerWidth = 240;

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path?: string;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Customers', icon: <PeopleIcon />, path: '/customers' },
  { text: 'Deployments', icon: <CloudIcon />, path: '/deployments' },
  { 
    text: 'Policies', 
    icon: <SecurityIcon />, 
    children: [
      { text: 'Policy Catalog', icon: <SecurityIcon />, path: '/policies' },
      { text: 'Policy Gap Report', icon: <SecurityIcon />, path: '/policy-gap-report' },
      { text: 'Policy Maintenance', icon: <SecurityIcon />, path: '/policy-maintenance' }
    ]
  },
  {
    text: 'Mappings',
    icon: <MapIcon />,
    children: [
      { text: 'Feature Catalog', icon: <CategoryIcon />, path: '/feature-catalog' },
      { text: 'Plan Catalog', icon: <ViewListIcon />, path: '/plan-catalog' },
      { text: 'Plan Mappings', icon: <MapIcon />, path: '/plan-mappings' }
    ]
  },
  { text: 'System Health', icon: <MonitorIcon />, path: '/system-health' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  { text: 'Migrations', icon: <MigrationsIcon />, path: '/migrations' },
];

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  
  // Get user data from MSAL accounts instead of localStorage
  const account = accounts[0];
  const visibleNavItems = navItems.filter(item => config.isDevelopment || item.path !== '/migrations');
  const userName = account?.name || 'Admin User';
  const userEmail = account?.username || '';
  const userInitial = userName.charAt(0).toUpperCase();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    instance.logoutRedirect().catch(e => {
      console.error('Logout error:', e);
    });
  };

  const handleToggleExpand = (text: string) => {
    setExpandedItems(prev => 
      prev.includes(text) 
        ? prev.filter(item => item !== text)
        : [...prev, text]
    );
  };

  const renderNavItem = (item: NavItem, level = 0): JSX.Element => {
    const isExpanded = expandedItems.includes(item.text);

    if (item.children) {
      return (
        <React.Fragment key={item.text}>
          <ListItemButton onClick={() => handleToggleExpand(item.text)} sx={{ pl: level * 2 }}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map(child => renderNavItem(child, level + 1))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <ListItemButton key={item.text} component={RouterLink} to={item.path || '/'} sx={{ pl: level * 2 }} onClick={() => setMobileOpen(false)}>
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItemButton>
    );
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          TC Config Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {visibleNavItems.map(item => renderNavItem(item))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` }
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            TC Config Admin
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
              {userName}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {userInitial}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem disabled>
                <Box>
                  <Typography variant="body2">{userName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {userEmail}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleMenuClose}>
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
